import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { enqueueSnackbar } from "notistack";
import { placeOrder } from "../../../Services/Https/orders";
import { verifyReCAPTCHA } from "../../../Services/Https/recaptcha";
import { IPlaceOrder, IPlaceOrderProduct } from "../../../Model/Order";
import { IPaymentRequestData } from "../../../Model/NestpayApi";
import ScrollToTop from "../../ScrollToTop/ScrollToTop";
import ReCAPTCHA from "react-google-recaptcha";
import "../../../Styles/OrderInformation.css";

export interface ISingleCartProductProps {
  productID: number;
  productName: string;
  productMaterial: string;
  productPrice: number;
  productImage: string;
  productAmount: number;
}

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    "& fieldset": { borderColor: "#ece7de" },
    "&:hover fieldset": { borderColor: "#c6912e" },
    "&.Mui-focused fieldset": { borderColor: "#c6912e" },
  },
  "& label.Mui-focused": { color: "#c6912e" },
};

const radioSx = {
  color: "#c6912e",
  "&.Mui-checked": { color: "#c6912e" },
};

const SingleCartProduct = ({
  productID,
  productName,
  productMaterial,
  productPrice,
  productImage,
  productAmount,
}: ISingleCartProductProps): JSX.Element => {
  const totalProductPrice = productPrice * productAmount;

  return (
    <div
      className="oi-item"
      onClick={() => {
        window.open(
          `https://jewelryshopdoro.com/products/${productID}`,
          "_blank"
        );
      }}
    >
      <img
        className="oi-item-image"
        src={`https://jewelryshopdoro.com/uploads/${productImage}`}
        alt={productName}
      />
      <div className="oi-item-info">
        <span className="oi-item-name">
          {productName} - {productMaterial}
        </span>
        <span className="oi-item-price">
          {`${productAmount} × ${productPrice} rsd = `}
          <span className="oi-item-total">
            {`${totalProductPrice.toFixed(2)} rsd`}
          </span>
        </span>
      </div>
    </div>
  );
};

const OrderInformation = () => {
  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [email, setEmail] = useState("");
  const [telephone, setTelephone] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [municipality, setMunicipality] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [paymentType, setPaymentType] = useState("");
  const [deliveryType, setDeliveryType] = useState("");
  const [isBusy, setIsBusy] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();
  const cartProductsString = sessionStorage.getItem("products");
  const customerNote = sessionStorage.getItem("customerNote");
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsDialogOpen, setTermsDialogOpen] = useState(false);

  const [transactionDialogOpen, setTransactionDialogOpen] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem("products") === null) {
      navigate("/");
    }
  }, [navigate]);

  const handlePaymentTypeChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPaymentType(event.target.value);
  };

  const handleDeliveryTypeChange = (event: ChangeEvent<HTMLInputElement>) => {
    setDeliveryType(event.target.value);
  };

  const handleTransactionDialogOpen = () => {
    setTransactionDialogOpen(true);
  };

  const handleTransactionDialogClose = () => {
    setTransactionDialogOpen(false);
  };

  const submitFormData = async (paymentRequestData: IPaymentRequestData) => {
    const form = document.createElement("form");
    form.method = "POST";
    form.action = "https://bib.eway2pay.com/fim/est3Dgate";

    for (const [key, value] of Object.entries(paymentRequestData)) {
      const hiddenField = document.createElement("input");
      hiddenField.type = "hidden";
      hiddenField.name = key;
      hiddenField.value = value;

      form.appendChild(hiddenField);
    }

    document.body.appendChild(form);
    form.submit();
  };

  const handleTermsCheckboxChange = () => {
    setTermsAccepted((prev) => !prev);
  };

  const handleTermsDialogOpen = () => {
    setTermsDialogOpen(true);
  };

  const handleTermsDialogClose = () => {
    setTermsDialogOpen(false);
  };

  const handleTermsDialogAccept = () => {
    setTermsDialogOpen(false);
    setTermsAccepted(true);
  };

  const handleSubmitOrderInformation = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    // reCAPTCHA validation
    if (!recaptchaRef.current) {
      enqueueSnackbar("Niste prošli reCAPTCHA proveru", {
        variant: "error",
      });
      setIsSubmitting(false);
      return;
    }
    const token = await recaptchaRef.current.executeAsync();
    recaptchaRef.current.reset();

    if (!token) {
      enqueueSnackbar("Niste prošli reCAPTCHA proveru", {
        variant: "error",
      });
      setIsSubmitting(false);
      return;
    }

    // Do verification with your backend using the token
    const reCAPTCHAVerificationSuccessful = await verifyReCAPTCHA(token);

    if (!reCAPTCHAVerificationSuccessful) {
      enqueueSnackbar("Niste prošli reCAPTCHA proveru", {
        variant: "error",
      });
      setIsSubmitting(false);
      return;
    }

    setIsBusy(true);
    // Input validation
const emailRegex = /^\s*[^\s@]+@[^\s@]+\.[^\s@]+\s*$/;

    if (
      !name ||
      !surname ||
      !telephone ||
      !address ||
      !city ||
      !municipality ||
      !postalCode
    ) {
      setIsBusy(false);
      setIsSubmitting(false);
      enqueueSnackbar("Morate popuniti sva označena polja", {
        variant: "error",
      });
      return;
    }
    if (email && !emailRegex.test(email)) {
      setIsBusy(false);
      setIsSubmitting(false);
      enqueueSnackbar("Morate napisati validnu email adresu", {
        variant: "error",
      });
      return;
    }
    // Email is required if paying with the credit card
    if (!email && paymentType === "with_card") {
      setIsBusy(false);
      setIsSubmitting(false);
      enqueueSnackbar("Email je obavezno polje ukoliko plaćate karticom", {
        variant: "error",
      });
      return;
    }
    if (!cartProductsString) {
      setIsBusy(false);
      setIsSubmitting(false);
      enqueueSnackbar("Ne možete napraviti porudžbinu sa 0 proizvoda", {
        variant: "error",
      });
      return;
    }

    if (isNaN(+postalCode)) {
      setIsBusy(false);
      setIsSubmitting(false);
      enqueueSnackbar("Postanski kod mora biti broj", { variant: "error" });
      return;
    }

    if (!termsAccepted) {
      setIsBusy(false);
      setIsSubmitting(false);
      enqueueSnackbar("Molimo Vas prihvatite uslove korišćenja", {
        variant: "error",
      });
      return;
    }

    if (paymentType === "") {
      setIsBusy(false);
      setIsSubmitting(false);
      enqueueSnackbar("Molimo Vas odaberite način plaćanja", {
        variant: "error",
      });
      return;
    }

    if (deliveryType === "") {
      setIsBusy(false);
      setIsSubmitting(false);
      enqueueSnackbar("Molimo Vas odaberite način isporuke", {
        variant: "error",
      });
      return;
    }

    // Mapping field names to match the RESTful API
    const cartProducts = JSON.parse(cartProductsString).map(
      (tmpProduct: any) => {
        const newProduct: IPlaceOrderProduct = {
          product_id: parseInt(tmpProduct.id),
          amount: tmpProduct.amount,
          color: tmpProduct.color,
        };
        return newProduct;
      }
    );

    // Send order to the server
    const orderInformation: IPlaceOrder = {
      customer_name: name,
      customer_surname: surname,
      email: email ? email.trim() : null,
      telephone_number: telephone,
      adress: `${address}, ${city}`,
      municipality: municipality,
      postal_code: postalCode,
      payment_type: paymentType === "with_card" ? "card" : "cash",
      delivery: deliveryType === "store" ? "store" : "shipping",
      note: customerNote ? customerNote : null,
      products: cartProducts,
    };

    try {
      const result = await placeOrder(orderInformation);

      sessionStorage.setItem(
        "orderInformation",
        JSON.stringify(orderInformation)
      );

      if (paymentType === "with_card") {
        // sessionStorage.removeItem("orderInformation");
        // sessionStorage.removeItem("customerNote");
        await submitFormData(result.paymentRequestData);
      } else {
        enqueueSnackbar("Porudžbina uspešno kreirana", {
          variant: "default",
        });

        navigate("/order-final", {
          state: {
            openPermition: true,
          },
        });
      }
    } catch (err) {
      enqueueSnackbar("Greška prilikom kreiranja porudžbine", {
        variant: "error",
      });
      setIsBusy(false);
      setIsSubmitting(false);
      return;
    }
  };

  if (cartProductsString) {
    const cartProducts = JSON.parse(cartProductsString);
    let totalOrderPrice = 0.0;
    for (let singleCartProduct of cartProducts) {
      totalOrderPrice += singleCartProduct.price * singleCartProduct.amount;
    }

    return (
      <div className="oi-page">
        <ReCAPTCHA
          ref={recaptchaRef}
          size="invisible"
          sitekey="6LdgZhInAAAAACFPo3lBFERmu0XEkmqVSeEpMvCw"
        />
        {isBusy ? (
          <div className="oi-busy">
            <span className="oi-busy-title">Obrada zahteva</span>
            <CircularProgress sx={{ color: "#c6912e" }} />
          </div>
        ) : null}
        <ScrollToTop />

        <p className="oi-breadcrumb">Početna → Poručivanje</p>
        <h1 className="oi-title">Poručivanje</h1>

        <div className="oi-layout">
          <div className="oi-main">
            <h2 className="oi-section-title">Podaci o kupcu</h2>
            <p className="oi-section-sub">
              Unesite Vaše podatke ispravno kako bi isporuka bila uspešna
            </p>

            <div className="oi-form">
              <div className="oi-field-row">
                <TextField
                  onChange={(e) => setName(e.target.value)}
                  sx={fieldSx}
                  fullWidth
                  label="Ime"
                  required
                  inputProps={{ maxLength: 50 }}
                />
                <TextField
                  onChange={(e) => setSurname(e.target.value)}
                  sx={fieldSx}
                  fullWidth
                  label="Prezime"
                  required
                  inputProps={{ maxLength: 50 }}
                />
              </div>
              <TextField
                onChange={(e) => setEmail(e.target.value)}
                sx={fieldSx}
                fullWidth
                label="Email adresa"
                inputProps={{ maxLength: 50 }}
              />
              <TextField
                onChange={(e) => setTelephone(e.target.value)}
                sx={fieldSx}
                fullWidth
                label="Broj telefona"
                required
                inputProps={{ maxLength: 15 }}
              />
              <TextField
                onChange={(e) => setAddress(e.target.value)}
                sx={fieldSx}
                fullWidth
                label="Ulica i broj"
                required
                inputProps={{ maxLength: 50 }}
              />
              <TextField
                onChange={(e) => setCity(e.target.value)}
                sx={fieldSx}
                fullWidth
                label="Grad"
                required
                inputProps={{ maxLength: 40 }}
              />
              <TextField
                onChange={(e) => setMunicipality(e.target.value)}
                sx={fieldSx}
                fullWidth
                label="Opština"
                required
                inputProps={{ maxLength: 50 }}
              />
              <TextField
                onChange={(e) => setPostalCode(e.target.value)}
                sx={fieldSx}
                fullWidth
                label="Poštanski broj"
                required
                inputProps={{ maxLength: 10 }}
              />
            </div>

            <h2 className="oi-section-title">Način plaćanja</h2>
            <RadioGroup
              className="oi-options"
              value={paymentType}
              onChange={handlePaymentTypeChange}
            >
              <FormControlLabel
                className={`oi-option ${
                  paymentType === "on_delivery" ? "is-selected" : ""
                }`}
                value="on_delivery"
                control={<Radio sx={radioSx} />}
                label={<span className="oi-option-label">Pouzećem</span>}
              />
              <FormControlLabel
                className={`oi-option ${
                  paymentType === "with_card" ? "is-selected" : ""
                }`}
                value="with_card"
                control={<Radio sx={radioSx} />}
                label={
                  <span className="oi-option-label">Platnom karticom</span>
                }
              />
            </RadioGroup>
            {paymentType === "with_card" ? (
              <button
                type="button"
                className="oi-link"
                onClick={handleTransactionDialogOpen}
              >
                Zaštita poverljivih podataka o transakciji
              </button>
            ) : null}

            <h2 className="oi-section-title">Način dostave</h2>
            <RadioGroup
              className="oi-options"
              value={deliveryType}
              onChange={handleDeliveryTypeChange}
            >
              <FormControlLabel
                className={`oi-option ${
                  deliveryType === "shipping" ? "is-selected" : ""
                }`}
                value="shipping"
                control={<Radio sx={radioSx} />}
                label={
                  <span className="oi-option-label">
                    Na željenu adresu (kupac snosi troškove)
                  </span>
                }
              />
              <FormControlLabel
                className={`oi-option ${
                  deliveryType === "store" ? "is-selected" : ""
                }`}
                value="store"
                control={<Radio sx={radioSx} />}
                label={
                  <span className="oi-option-label">
                    Preuzimanje u radnji u Novom Sadu
                  </span>
                }
              />
            </RadioGroup>

            <FormControlLabel
              className="oi-terms"
              control={
                <Checkbox
                  checked={termsAccepted}
                  onChange={handleTermsCheckboxChange}
                  sx={radioSx}
                />
              }
              label={
                <button
                  type="button"
                  className="oi-link"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleTermsDialogOpen();
                  }}
                >
                  Pročitao/la sam i prihvatam uslove korišćenja
                </button>
              }
            />

            <button
              type="button"
              className="oi-submit-btn"
              disabled={isSubmitting}
              onClick={() => handleSubmitOrderInformation()}
            >
              Potvrda i plaćanje
            </button>

            <p className="oi-courier-note">
              Slanje putem kurirske službe. Rok isporuke: 1-3 radna dana od
              slanja. Poštarina nije uključena u cenu, i biće blago uvećana u
              slučaju plaćanja pouzećem, u zavisnosti od vrednosti paketa.
            </p>
          </div>

          <aside className="oi-summary">
            <h2 className="oi-summary-title">Vaša porudžbina</h2>
            <div className="oi-summary-list">
              {cartProducts.map((singleCartProduct: any, index: number) => (
                <SingleCartProduct
                  key={index}
                  productID={singleCartProduct.id}
                  productName={singleCartProduct.product_name}
                  productMaterial={singleCartProduct.material}
                  productPrice={singleCartProduct.price}
                  productImage={singleCartProduct.main_image}
                  productAmount={singleCartProduct.amount}
                />
              ))}
            </div>
            <div className="oi-summary-total">
              {`Ukupna cena porudžbine: ${totalOrderPrice.toFixed(2)} rsd`}
            </div>
            <p className="oi-summary-note">
              (PDV uračunat u cenu i nema skrivenih troškova)
            </p>
          </aside>
        </div>
        <Dialog open={termsDialogOpen} onClose={handleTermsDialogClose}>
          <DialogTitle>Uslovi korišćenja</DialogTitle>
          <DialogContent>
            <Typography variant="h6">
              1. Prihvatanje Uslova korišćenja
            </Typography>
            <Typography>
              Korišćenjem naše web prodavnice, prihvatate ove Uslove korišćenja.
              Ako se ne slažete sa ovim Uslovima korišćenja, molimo vas da ne
              koristite našu web prodavnicu. <br/>
              Kupovina putem našeg sajta https://jewelryshopdoro.com/ smatra se zaključenjem ugovora na 
              daljinu u skladu sa Zakonom o zaštiti potrošača – prečišćen tekst ("Sl. glasnik RS", br. 62/2014, 6/2016 - dr. zakon, 44/2018, 30/2023)<br/>
              Korisnik potvrđuje da je pre kupovine pročitao, razumeo i prihvatio ove uslove korišćenja.
            </Typography><br/>
            <Typography variant="h6">2. Osnovni podaci o trgovcu</Typography>
            <Typography>
              Pun naziv pravnog subjekta: VERICA JARČEVIĆ PREDUZETNIK, SZTR DAROTEKA DORO NOVI SAD <br />
              Adresa: Ise Bajića 8, 21000 Novi Sad <br />
              Delatnost i šifra delatnosti: 3212 - Proizvodnja nakita i srodnih
              predmeta <br /> Matični broj: 52404860 <br /> Poreski broj:
              101705556 <br /> Kontakt telefon: +381605500750 <br /> Kontakt
              e-mail: silvershopdoro@gmail.com
            </Typography><br/>
            <Typography variant="h6">3. Izjava o konverziji</Typography>
            <Typography>
              Sva plaćanja će biti izvršena isključivo u lokalnoj valuti Republike Srbije - dinar (RSD). 
              Cene proizvoda prikazane na našem sajtu takođe su izražene u dinarima. Molimo vas da imate u vidu da se iznos koji
               će biti zadužen na vašoj platnoj kartici dobija 
               konverzijom cene iz dinara u vašu lokalnu valutu po
                kursu koji koriste kartičarske organizacije. 
                Kao rezultat konverzije, može doći do razlike u 
                naplaćenom iznosu. Ove razlike u odnosu na cenu 
                proizvoda ili usluge objavljene na našem sajtu, 
                nisu pod kontrolom trgovca i ne predstavljaju osnov 
                za reklamaciju.
            </Typography><br/>
            <Typography variant="h6">
              4. Kontakt podaci - korisnički servis
            </Typography>
            <Typography>
              Kontakt telefon: +381605500750 <br />
              Kontakt e-mail: silvershopdoro@gmail.com
            </Typography><br/>
            <Typography variant="h6">
              5. Dostava robe 
            </Typography>
            <Typography>
            - Kurirska služba: Dostava robe vrši se na teritoriji Republike Srbije putem kurirske službe koja isporučuje robu na navedenoj teritoriji. 
            <b> Prodavac, iz razloga efikasnosti i brzine isporuke, zadržava pravo da izabere kurirsku službu koja je u datom trenutku dostupna za najbržu dostavu.</b> Kupac će o nazivu kurirske službe biti obavešten putem e-maila nakon potvrde porudžbine.<br />
            - Troškove dostave snosi potrošač, u skladu sa važećim cenovnikom kurirske službe na dan isporuke. Ukoliko potrošač odabere opciju plaćanja pouzećem, ukupna cena porudžbine može biti uvećana za trošak obrade otkupnine, u skladu sa pravilima kurirske službe.<br />
            - Rok dostave: Ukoliko poručeni proizvod trenutno nije dostupan na lageru u željenoj količini,
            vreme pripreme koje uključuje proizvodnju može trajati do 10 radnih dana. Nakon otpreme, isporuka se obično vrši u roku od 1 do 3 radna dana. Ukupan rok isporuke, računajući od dana prijema porudžbine, iznosi najviše 14 radnih dana. U slučaju vanrednih okolnosti koje onemogućavaju ispunjenje ovog roka (kao što su viša sila, logistički zastoji i sl.), potrošač će biti blagovremeno obavešten i imaće pravo da odustane od ugovora bez dodatnih troškova.

            </Typography><br/>
             <Typography variant="h6">
              6. Pravo na odustanak od ugovora na daljinu
            </Typography>
            <Typography>
              Zakon za slučaj prodaje na daljinu ustanovljava pravo kupca, koji se smatra potrošačem (fizičko lice koje proizvod kupuje radi namirenja svojih individualnih potreba, 
              a ne radi obavljanja profesionalne delatnosti), da odustane od ugovora u roku od 14 dana
               od dana kada mu je proizvod predat. Prilikom odustanka kupac može, ali ne mora da navede razloge 
               zbog kojih odustaje. Da biste ostvarili pravo na odustanak, potrebno je da popunite Obrazac za odustanak od ugovora 
               (Odustanica) i dostavite ga na našu e-mail adresu: silvershopdoro@gmail.com, ili poštom - 
               na adresu VERICA JARČEVIĆ PREDUZETNIK, SZTR DAROTEKA DORO NOVI SAD, NIKOLE PAŠIĆA 7, 21000 NOVI SAD. Odustanica je automatski dostavljena kupcu uz e-mail potvrde.
               <br/>
               <br/>
                <b>PRAVO NA ODUSTANAK NE POSTOJI</b>, ako je proizvod rađen po specifikaciji kupca ili je personalizovan (član 36  stav 1 tačka 3 Zakona o zaštiti potrošača). Ovo se odnosi, na primer, na proizvode izrađene po narudžbini, sa ugraviranim tekstom, specifičnim dimenzijama ili prilagođenim dizajnom.<br/>
           <br/> <Typography variant="h6">
              Troškovi povraćaja robe
            </Typography>
                Kupac snosi direktne troškove vraćanja robe. Prilikom povraćaja, kupljeni nakit mora biti vraćen neoštećen i sa nepromenjenim svojstvima (dakle, da nije korišćen ili oštećen), adekvatno upakovan – bilo u originalnoj, bilo u sličnoj zaštitnoj ambalaži koja omogućava siguran transport, uz prateću dokumentaciju i fiskalni račun.
           <Typography variant="h6">
              Povraćaj novca
            </Typography>
            U roku od 14 dana od prijema izjave o odustanku i vraćene robe, izvršićemo povraćaj svih
            uplata, uključujući troškove isporuke, osim dodatnih troškova koji su nastali po posebnom zahtevu kupca. Povraćaj sredstava vrši se na račun koji kupac navede u Odustanici.
            </Typography><br/>
            <Typography variant="h6">7. Reklamacije</Typography>
            <Typography>
              - Potrošač može da izjavi reklamaciju radi ostvarivanja svojih prava, 
              u skladu sa Zakonom o zaštiti potrošača.
              <br />
              - Reklamacija se odnosi isključivo na proizvode kupljene 
              putem naše web prodavnice, uz obavezno priložen dokaz o kupovini (fiskalni račun ili potvrda transakcije)
              <br />
              - Reklamacije će se uzeti u razmatranje samo ako je proizvod ili usluga neispravna, 
              oštećena ili ne odgovara opisu koji je pružen na n
              ašem sajtu i u slučaju skrivenih mana, nesaobraznosti i nefunkcionalnosti.
              <br />
              - Da biste podneli reklamaciju, molimo vas da nas kontaktirate putem naših dostupnih kanala za korisničku podršku (telefon, e-mail).
              <br />
              - Naš tim će vam pružiti detaljne instrukcije o daljem postupku, uključujući način vraćanja proizvoda ili dogovor o zameni.
              <br />
              - U nekim slučajevima može biti potrebno vratiti proizvod pre nego što bude odobrena zamena ili povraćaj novca.

              <br />
              - Nakon što primimo vašu reklamaciju, pažljivo ćemo je pregledati i doneti odluku o prihvatanju ili odbijanju reklamacije u skladu sa relevantnim zakonima i propisima.

              <br />
              - Trgovac je dužan da potrošaču odgovori na izjavljenu reklamaciju najkasnije u roku od 8 dana od dana prijema reklamacije
              <br />
              - U slučaju da reklamacije bude prihvaćena prodavac će obavestiti potrošača da ima pravo da bira između zamene, popravke ili raskida ugovora u slučaju nesaobraznosti (član 51 Zakon o zaštiti potrošača)
              <br />
              - Ukoliko reklamacija bude odbijena, obavestićemo vas o razlozima za odbijanje i pružiti vam objašnjenje.
    
            </Typography>
           
            <Typography variant="h6"><br/>
              8. Zaštita privatnosti korisnika
            </Typography>
            <Typography>
              U ime trgovca obavezujemo se da ćemo čuvati privatnost svih naših potrošača.
               Prikupljamo samo neophodne osnovne podatke o kupcima/korisnicima i podatke neophodne 
               za poslovanje i informisanje korisnika u skladu sa dobrim poslovnim običajima i
                u cilju pružanja kvalitetne usluge. Dajemo kupcima mogućnost izbora, uključujući mogućnost
                 odluke da li žele ili ne žele da budu uključeni na mailing listu koja se koristi za marketinške kampanje. Svi podaci o 
                 korisnicima/kupcima se strogo čuvaju i dostupni su samo zaposlenima kojima su ti podaci neophodni za obavljanje posla.
                  Svi članovi preduzeća trgovca kao i poslovni partneri trgovca odgovorni su za poštovanje načela zaštite privatnosti.
            </Typography><br/>
            <Typography variant="h6">9. Zaštita poverljivih podataka o transakciji</Typography>
            <Typography>
             Prilikom unošenja podataka o platnoj kartici, poverljive informacije se prenose putem javne mreže u 
             zaštićenoj (kriptovanoj) formi upotrebom SSL protokola i PKI sistema, kao trenutno najsavremenije kriptografske tehnologije. Sigurnost podataka prilikom kupovine garantuje procesor platnih kartica, Banca Intesa ad Beograd, pa se tako kompletni proces
              naplate obavlja na stranicama banke. Niti jednog trenutka podaci o platnoj kartici nisu dostupni našem sistemu.
            </Typography><br/>
            <Typography variant="h6">10. Povraćaj sredstava</Typography>
            <Typography>
             U slučaju vraćanja robe i povraćaja sredstava kupcu koji je prethodno platio nekom od platnih kartica,
              delimično ili u celosti, a bez obzira na razlog vraćanja, trgovac je u obavezi da povraćaj vrši isključivo putem VISA, EC/MC, Maestro, Amex i Dina metoda plaćanja, što
            </Typography><br/>
             <Typography variant="h6">11. Izjava o PDV-u</Typography>
            <Typography>
            Sve cene na sajtu uključuju PDV i predstavljaju konačne iznose. Ne postoje dodatni ili skriveni troškovi.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleTermsDialogClose} color="secondary">
              Zatvori
            </Button>
            <Button onClick={handleTermsDialogAccept} color="secondary">
              Prihvati
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={transactionDialogOpen}
          onClose={handleTransactionDialogClose}
        >
          <DialogTitle>Zaštita poverljivih podataka o transakciji</DialogTitle>
          <DialogContent>
            <Typography>
              Prilikom unošenja podataka o platnoj kartici, poverljive
              informacija se prenose putem javne mreže u zaštićenoj
              (kriptovanoj) formi upotrebom SSL protokola i PKI sistema, kao
              trenutno najsavremenije kriptografske tehnologije. Sigurnost
              podataka prilikom kupovine, garantuje procesor platnih kartica,
              Banca Intesa ad Beograd, pa se tako kompletni proces naplate
              obavlja na stranicama banke. Niti jednog trenutka podaci o platnoj
              kartici nisu dostupni našem sistemu.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleTransactionDialogClose} color="secondary">
              Zatvori
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  } else {
    return (
      <div className="oi-page">
        <div className="oi-empty">Vaša korpa je prazna</div>
      </div>
    );
  }
};

export default OrderInformation;
